import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  alpha,
  useTheme
} from '@mui/material';
import {
  LocalShipping as ShippingIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  TrackChanges as TrackingIcon,
  Business as BusinessIcon,
  Warning as WarningIcon,
  Description as DocumentIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';

const DeliveryInfoSection = ({ data }) => {
  const theme = useTheme();
  const { deliveryInfo } = data;

  if (!deliveryInfo) {
    return (
      <Card sx={{ 
        mb: 3,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 20px ${alpha(theme.palette.grey[500], 0.15)}`
        }
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              bgcolor: alpha(theme.palette.warning.main, 0.1)
            }}>
              <ShippingIcon sx={{ fontSize: 28, color: 'warning.main' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
                Delivery Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No delivery information available
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Please complete the Delivery Information step to view shipping details.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const getDeliveryStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered': return 'success';
      case 'in_transit': return 'info';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      case 'delayed': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // If it's already a formatted date string, return it
    if (typeof dateString === 'string' && dateString.includes('/')) {
      return dateString;
    }
    // Parse date string (YYYY-MM-DD) directly without timezone conversion
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        return `${month}/${day}/${year}`;
      }
    }
    // Fallback: use Date but add noon to avoid timezone issues
    const date = new Date(dateString + 'T12:00:00');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const formatAddress = (street, city, state, zip, country) => {
    const parts = [];
    if (street) parts.push(street);
    
    const cityStateZip = [];
    if (city) cityStateZip.push(city);
    if (state) cityStateZip.push(state);
    if (zip) cityStateZip.push(zip);
    if (cityStateZip.length > 0) parts.push(cityStateZip.join(', '));
    
    if (country) parts.push(country);
    return parts.join('\n');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <Card sx={{ 
      mb: 3,
      borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 20px ${alpha(theme.palette.grey[500], 0.15)}`
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            bgcolor: alpha(theme.palette.info.main, 0.1)
          }}>
            <ShippingIcon sx={{ fontSize: 28, color: 'info.main' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Delivery Information
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Shipping and logistics details
            </Typography>
          </Box>
          <Chip 
            label={(() => {
              const status = deliveryInfo.delivery_status || 'pending';
              // Capitalize first letter
              return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
            })()} 
            color={getDeliveryStatusColor(deliveryInfo.delivery_status)}
            sx={{ fontWeight: 500, px: 2, py: 2.5, fontSize: '0.85rem' }}
          />
        </Box>

        {/* BOL and Tracking Info */}
        <Box sx={{ 
          p: 2, 
          borderRadius: 2, 
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          mb: 3
        }}>
          <Grid container spacing={2}>
            {deliveryInfo.bol_number && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DocumentIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase' }}>
                      BOL Number
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {deliveryInfo.bol_number}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
            {deliveryInfo.tracking_number && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrackingIcon sx={{ fontSize: 18, color: 'info.main' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase' }}>
                      Tracking Number
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {deliveryInfo.tracking_number}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
            {deliveryInfo.carrier_name && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon sx={{ fontSize: 18, color: 'success.main' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase' }}>
                      Carrier
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {deliveryInfo.carrier_name}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
            {deliveryInfo.container_number && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase' }}>
                      Container Number
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {deliveryInfo.container_number}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Dates */}
        {(deliveryInfo.ship_date || deliveryInfo.expected_delivery_date || deliveryInfo.actual_delivery_date) && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={500} sx={{ mb: 2, color: 'primary.main' }}>
              Shipping Timeline
            </Typography>
            <Grid container spacing={2}>
              {deliveryInfo.ship_date && (
                <Grid item xs={12} sm={4}>
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: alpha(theme.palette.info.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CalendarIcon sx={{ fontSize: 16, color: 'info.main' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase' }}>
                        Ship Date
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {formatDate(deliveryInfo.ship_date)}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {deliveryInfo.expected_delivery_date && (
                <Grid item xs={12} sm={4}>
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: alpha(theme.palette.warning.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CalendarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase' }}>
                        Expected
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {formatDate(deliveryInfo.expected_delivery_date)}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {deliveryInfo.actual_delivery_date && (
                <Grid item xs={12} sm={4}>
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: alpha(theme.palette.success.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CalendarIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase' }}>
                        Delivered
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {formatDate(deliveryInfo.actual_delivery_date)}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Origin and Destination */}
        <Grid container spacing={3}>
          {/* Origin */}
          {(deliveryInfo.shipper_name || deliveryInfo.origin_street) && (
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: alpha(theme.palette.success.main, 0.03),
                border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`
              }}>
                <Typography variant="subtitle2" fontWeight={500} sx={{ mb: 2, color: 'success.main' }}>
                  ORIGIN (SHIPPER)
                </Typography>
                {deliveryInfo.shipper_name && (
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                    {deliveryInfo.shipper_name}
                  </Typography>
                )}
                {deliveryInfo.shipper_contact && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Contact: {deliveryInfo.shipper_contact}
                  </Typography>
                )}
                {deliveryInfo.shipper_email && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Email: {deliveryInfo.shipper_email}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 2 }}>
                  <LocationIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.3 }} />
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {formatAddress(
                      deliveryInfo.origin_street,
                      deliveryInfo.origin_city,
                      deliveryInfo.origin_state,
                      deliveryInfo.origin_zip,
                      deliveryInfo.origin_country
                    )}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {/* Destination */}
          {(deliveryInfo.consignee_name || deliveryInfo.destination_street) && (
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: alpha(theme.palette.warning.main, 0.03),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`
              }}>
                <Typography variant="subtitle2" fontWeight={500} sx={{ mb: 2, color: 'warning.main' }}>
                  DESTINATION (CONSIGNEE)
                </Typography>
                {deliveryInfo.consignee_name && (
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                    {deliveryInfo.consignee_name}
                  </Typography>
                )}
                {deliveryInfo.consignee_company && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Company: {deliveryInfo.consignee_company}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 2 }}>
                  <LocationIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.3 }} />
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {formatAddress(
                      deliveryInfo.destination_street,
                      deliveryInfo.destination_city,
                      deliveryInfo.destination_state,
                      deliveryInfo.destination_zip,
                      deliveryInfo.destination_country
                    )}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Cargo Details */}
        {(deliveryInfo.num_pallets || deliveryInfo.num_cases || deliveryInfo.total_weight) && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" fontWeight={500} sx={{ mb: 2, color: 'primary.main' }}>
              Cargo Details
            </Typography>
            <Grid container spacing={2}>
              {deliveryInfo.num_pallets > 0 && (
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                    <Typography variant="h6" fontWeight={500} color="info.main">
                      {deliveryInfo.num_pallets}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Pallets
                    </Typography>
                  </Box>
                </Grid>
              )}
              {deliveryInfo.num_cases > 0 && (
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                    <Typography variant="h6" fontWeight={500} color="success.main">
                      {deliveryInfo.num_cases}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Cases
                    </Typography>
                  </Box>
                </Grid>
              )}
              {deliveryInfo.num_units > 0 && (
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                    <Typography variant="h6" fontWeight={500} color="warning.main">
                      {deliveryInfo.num_units}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Units
                    </Typography>
                  </Box>
                </Grid>
              )}
              {deliveryInfo.total_weight > 0 && (
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                    <Typography variant="h6" fontWeight={500} color="primary.main">
                      {deliveryInfo.total_weight} {deliveryInfo.weight_unit || 'lbs'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Weight
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Hazmat Warning */}
        {deliveryInfo.hazardous_materials && (
          <Box sx={{ 
            mt: 3,
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.error.main, 0.08),
            border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <WarningIcon sx={{ fontSize: 28, color: 'error.main' }} />
              <Box>
                <Typography variant="subtitle2" fontWeight={500} color="error.main">
                  HAZARDOUS MATERIALS
                </Typography>
                {deliveryInfo.hazmat_details && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {deliveryInfo.hazmat_details}
                  </Typography>
                )}
                {deliveryInfo.un_number && (
                  <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                    UN Number: {deliveryInfo.un_number}
                  </Typography>
                )}
                {deliveryInfo.hazard_class && (
                  <Typography variant="body2" fontWeight={500}>
                    Class: {deliveryInfo.hazard_class}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* Financial & Insurance */}
        {(deliveryInfo.freight_charges || deliveryInfo.insurance_required) && (
          <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.3)}` }}>
            <Grid container spacing={2}>
              {deliveryInfo.freight_charges > 0 && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MoneyIcon sx={{ fontSize: 18, color: 'success.main' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase' }}>
                        Freight Charges
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {formatCurrency(deliveryInfo.freight_charges)}
                      </Typography>
                      {deliveryInfo.payment_responsibility && (
                        <Typography variant="caption" color="text.secondary">
                          Paid by: {deliveryInfo.payment_responsibility}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              )}
              {deliveryInfo.insurance_required && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase', mb: 0.5 }}>
                    Insurance
                  </Typography>
                  {deliveryInfo.insurance_provider && (
                    <Typography variant="body2" fontWeight={500}>
                      Provider: {deliveryInfo.insurance_provider}
                    </Typography>
                  )}
                  {deliveryInfo.insurance_coverage > 0 && (
                    <Typography variant="body2">
                      Coverage: {formatCurrency(deliveryInfo.insurance_coverage)}
                    </Typography>
                  )}
                  {deliveryInfo.insurance_policy_number && (
                    <Typography variant="body2">
                      Policy: {deliveryInfo.insurance_policy_number}
                    </Typography>
                  )}
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Notes */}
        {(deliveryInfo.delivery_notes || deliveryInfo.special_handling) && (
          <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.3)}` }}>
            {deliveryInfo.special_handling && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase', mb: 1 }}>
                  Special Handling
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', p: 2, bgcolor: alpha(theme.palette.warning.main, 0.05), borderRadius: 2 }}>
                  {deliveryInfo.special_handling}
                </Typography>
              </Box>
            )}
            {deliveryInfo.delivery_notes && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase', mb: 1 }}>
                  Delivery Notes
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 2 }}>
                  {deliveryInfo.delivery_notes}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default DeliveryInfoSection;